import { phaseKey } from "../projects/projectUtils";
import type {
  Ending,
  IntroStep,
  OnboardingAccount,
  OnboardingStep,
  RoleGroup,
  SceneModel,
  Story,
  StoryDaily,
  StoryValue,
  TipStep,
} from "./types";

/** Une séquence par appareil : vue sur ordinateur, elle revient une fois sur téléphone, en stories. */
export const introStepFor = (isMobile: boolean): IntroStep =>
  isMobile ? "intro_mobile" : "intro_desktop";

export const shouldShowIntro = (
  seen: ReadonlySet<OnboardingStep>,
  isMobile: boolean,
) => !seen.has(introStepFor(isMobile));

/** Une bulle par écran, une fois, et seulement après la séquence de cet appareil. */
export const canShowTip = (
  seen: ReadonlySet<OnboardingStep>,
  step: TipStep,
  isMobile: boolean,
) => seen.has(introStepFor(isMobile)) && !seen.has(step);

/** Fin de la séquence : l'invité choisit sa zone puis voit l'offre ; les autres ouvrent leurs 10 du jour. */
export const endingFor = (account: OnboardingAccount): Ending =>
  account === "invited"
    ? { primary: "choose_zone", primaryTo: "/offer", secondaryTo: "/projects" }
    : { primary: "open_daily", primaryTo: "/", secondaryTo: "/projects" };

const GROUPS: Record<string, RoleGroup> = {
  "Client / Project Owner": "owner",
  Developer: "owner",
  Funder: "owner",
  "Engineering company": "design",
  Architect: "design",
  "Design consultant": "design",
  "Waterproofing consultant": "design",
  "Concrete consultant": "design",
  "Project management company": "design",
  "Main contractor": "contractor",
  "General contractor": "contractor",
  "EPC contractor": "contractor",
  "Civil contractor": "contractor",
  "Waterproofing contractor": "contractor",
};

/** Rôles de l'extraction (apps/worker/src/radar/extraction.py) en trois maillons de la chaîne de décision. */
export const roleGroup = (role: string): RoleGroup | null => GROUPS[role] ?? null;

// Exemples affichés faute de données, toujours marqués « Exemple » à l'écran.
// La valeur brute est en anglais, comme celles de l'extraction ; sa version française est à part.
const EXAMPLE_STRUCTURE_FR = "réservoir enterré";
const EXAMPLE_VALUES: StoryValue[] = [
  { field: "structure_type", value: "buried reservoir", method: "extracted" },
  { field: "budget_amount", value: "48500000 EUR", method: "extracted" },
  { field: "start_date", value: "2027-03", method: "inferred" },
];
const EXAMPLE_DAILY: StoryDaily[] = [
  { name: null, category: "water", country: "FR", phase: "Design", amount_band: "10-100", score: 91 },
  { name: null, category: "tunnels_rail", country: "IT", phase: "Feasibility study", amount_band: "500+", score: 88 },
  { name: null, category: "bridges_parking", country: "CH", phase: "Tender", amount_band: "100-500", score: 84 },
];
const EXAMPLE_ROLES = (["owner", "design", "contractor"] as const).map((group) => ({
  group,
  company: null,
}));

export const sceneModel = (story: Story | null): SceneModel => {
  const account = story?.account ?? "team";
  const blurred = account === "invited";
  const show = story?.showcase ?? null;
  const project = show?.kind === "project" ? show : null;

  const values = project?.values.length
    ? { items: project.values, structureTypeFr: project.structure_type_fr ?? null, example: false }
    : { items: EXAMPLE_VALUES, structureTypeFr: EXAMPLE_STRUCTURE_FR, example: true };

  const score =
    show?.score != null
      ? { value: show.score, criteria: project?.criteria ?? [], reasons: show?.kind === "teaser" ? show.reasons : [], example: false }
      : { value: 86, criteria: ["waterproofing_need", "structure_type", "phase"], reasons: [], example: true };

  const seenGroups = new Set<RoleGroup>();
  const roleItems = (project?.roles ?? []).flatMap(({ role, company }) => {
    const group = roleGroup(role);
    if (!group || seenGroups.has(group)) return [];
    seenGroups.add(group);
    return [{ group, company: blurred ? null : company }];
  });
  const roles = roleItems.length
    ? { items: roleItems, blurred, example: false }
    : { items: EXAMPLE_ROLES, blurred, example: true };

  const daily = story?.daily.length
    ? { items: story.daily, example: false }
    : { items: EXAMPLE_DAILY, example: true };

  return { account, counts: story?.counts ?? null, values, score, roles, daily };
};

const intlLocale = (locale: string) => (locale.startsWith("fr") ? "fr-FR" : "en-GB");

/** Date de début telle que l'extraction l'écrit (« 2027 », « 2027-03 », « 2027-03-15 »), dans la langue de l'écran. */
export const formatStartDate = (value: string, locale: string) => {
  const match = /^(\d{4})(?:-(\d{2}))?(?:-(\d{2}))?$/.exec(value.trim());
  if (!match) return value;
  const [, year, month, day] = match;
  if (!month) return year;
  const date = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day ?? 1)));
  return new Intl.DateTimeFormat(intlLocale(locale), {
    timeZone: "UTC",
    year: "numeric",
    month: "long",
    ...(day ? { day: "numeric" } : {}),
  }).format(date);
};

type FormatOptions = {
  locale: string;
  structureTypeFr: string | null;
  t: (key: string, options?: object) => string; // clés `resources.onboarding.*`
  tr: (fullKey: string, options?: object) => string; // clés complètes
};

/** Valeur tamponnée de la scène 2, lisible dans la langue de l'écran (les valeurs brutes sont en anglais). */
export const formatStoryValue = (v: StoryValue, { locale, structureTypeFr, t, tr }: FormatOptions) => {
  switch (v.field) {
    case "phase":
      return tr(phaseKey(v.value), { _: v.value });
    case "structure_type":
      return locale.startsWith("fr") && structureTypeFr ? structureTypeFr : v.value;
    case "start_date":
      return formatStartDate(v.value, locale);
    case "budget_amount": {
      const [amount, currency = ""] = v.value.split(" ");
      const n = Number(amount);
      if (!Number.isFinite(n)) return v.value;
      return t("units.millions", {
        amount: (n / 1e6).toLocaleString(intlLocale(locale), { maximumFractionDigits: 1 }),
        currency,
      }).trim();
    }
    default:
      return v.value;
  }
};
